/**
 * Dialog — responsive content overlay (ADR-114).
 *
 * API-compatible with the shadcn/Radix contract used across the apps:
 *
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent size="md">
 *       <DialogHeader><DialogTitle>…</DialogTitle></DialogHeader>
 *       <DialogFooter>…</DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 *
 * Responsive behavior: on viewports < SHEET_BREAKPOINT the content is a
 * bottom-sheet with drag-to-dismiss; from 640px up it is a centered dialog.
 * Pass `mobile="dialog"` to opt out of sheet mode for a specific overlay.
 */

import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../../lib/utils';
import type { OverlaySize } from './constants';
import { DiscardChangesDialog } from './DiscardChangesDialog';
import { OverlayShell } from './OverlayShell';
import { useBreakpoint } from './useBreakpoint';

interface DialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialogContext(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('Dialog parts must be used inside <Dialog>.');
  }
  return context;
}

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Dialog({ open, onOpenChange, defaultOpen = false, children }: DialogProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const value = useMemo<DialogContextValue>(
    () => ({ open: isControlled ? Boolean(open) : internalOpen, setOpen }),
    [isControlled, open, internalOpen, setOpen],
  );

  return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
}

export interface DialogTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: ReactNode;
}

export function DialogTrigger({ asChild, children, onClick, ...props }: DialogTriggerProps) {
  const { setOpen } = useDialogContext();

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ onClick?: (event: unknown) => void }>;
    return cloneElement(child, {
      onClick: (event: unknown) => {
        child.props.onClick?.(event);
        setOpen(true);
      },
    });
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        onClick?.(event);
        setOpen(true);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export interface DialogCloseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children?: ReactNode;
}

export function DialogClose({ asChild, children, onClick, ...props }: DialogCloseProps) {
  const { setOpen } = useDialogContext();

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ onClick?: (event: unknown) => void }>;
    return cloneElement(child, {
      onClick: (event: unknown) => {
        child.props.onClick?.(event);
        setOpen(false);
      },
    });
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        onClick?.(event);
        setOpen(false);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export interface DialogContentProps {
  children: ReactNode;
  className?: string;
  /** Width archetype on desktop; defaults to `md` (max-w-lg, legacy contract). */
  size?: OverlaySize;
  /** `auto` (default) = bottom-sheet on mobile; `dialog` = always centered. */
  mobile?: 'auto' | 'sheet' | 'dialog';
  dismissible?: boolean;
  /**
   * ADR-114 (enmienda 1): true when the form holds unsaved changes.
   * Backdrop click and drag-to-dismiss are blocked; ESC opens a discard
   * confirmation instead of closing. Explicit Cancel/Close buttons should
   * also confirm via `DiscardChangesDialog`.
   */
  dirty?: boolean;
  discardTitle?: string;
  discardMessage?: string;
  discardConfirmLabel?: string;
  discardCancelLabel?: string;
  showClose?: boolean;
  closeLabel?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  'data-testid'?: string;
}

export function DialogContent({
  children,
  className,
  size = 'md',
  mobile = 'auto',
  dismissible = true,
  dirty = false,
  discardTitle = 'Discard changes?',
  discardMessage = 'You have unsaved changes. If you close now, they will be lost.',
  discardConfirmLabel = 'Discard',
  discardCancelLabel = 'Keep editing',
  showClose = true,
  closeLabel = 'Close',
  ariaLabel,
  ariaLabelledBy,
  'data-testid': testId,
}: DialogContentProps) {
  const { open, setOpen } = useDialogContext();
  const { isMobile } = useBreakpoint();
  const [discardOpen, setDiscardOpen] = useState(false);
  const mode = mobile === 'dialog' ? 'dialog' : isMobile ? 'sheet' : 'dialog';

  // Reset the discard confirmation when the dialog closes.
  useEffect(() => {
    if (!open) setDiscardOpen(false);
  }, [open]);

  // When dirty, implicit dismissal is blocked (dismissible=false below) and
  // ESC asks for confirmation instead of closing.
  useEffect(() => {
    if (!open || !dirty || !dismissible) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDiscardOpen(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, dirty, dismissible]);

  return (
    <>
      <OverlayShell
        open={open}
        onClose={dirty ? () => setDiscardOpen(true) : () => setOpen(false)}
        dismissible={dismissible && !dirty}
        mode={mode}
        size={size}
        className={cn('sm:rounded-lg', className)}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
        dragDismiss={mode === 'sheet'}
        showClose={showClose}
        closeLabel={closeLabel}
        testId={testId}
      >
        {children}
      </OverlayShell>

      <DiscardChangesDialog
        open={open && discardOpen}
        onDiscard={() => {
          setDiscardOpen(false);
          setOpen(false);
        }}
        onCancel={() => setDiscardOpen(false)}
        title={discardTitle}
        message={discardMessage}
        confirmLabel={discardConfirmLabel}
        cancelLabel={discardCancelLabel}
      />
    </>
  );
}
